import { Box } from '@stenajs-webui/core';
import {
  Banner,
  stenaAnimals,
  stenaBusinessCard,
  stenaHazardous,
  stenaPlugin,
} from '@stenajs-webui/elements';
import type { FC } from 'react';
import type { DetailedBooking_autoCancellation } from '../../../../common/graphql/fragments/gql/DetailedBooking';
import { routeCreators } from '../../../../common/routes/AppRoutes';
import { Information } from '../../../../common/texts';
import type { ApprovalConfig } from '../../../../common/utils/approvalInfo';
import { GoodsDescriptionsEnsHintBanner } from '../../../../common-ui/goods/GoodsDescriptionsEnsHintBanner';
import {
  InformationMessageType,
  resolveInformationMessageBannerVariant,
} from '../../../../common-ui/information-message/informationMessageUtils';
import { FlatButtonLink } from '../../../../common-ui/Link';
import { AutoCancellationInfoBanner } from './AutoCancellationInfoBanner';

interface BookingInformationMessagesProps {
  arrivalNotepadInfo: ApprovalConfig | null;
  autoCancellation: DetailedBooking_autoCancellation | null;
  bookingNo: number;
  hazardousGoodsApprovalInfo: ApprovalConfig | null;
  hazardousGoodsApproved: boolean;
  livestockInfoText: string | undefined;
  loadingPreferenceApprovalInfo: ApprovalConfig | null;
  showEnsGoodsBanner: boolean;
  showHazGoodsPaymentInformation: boolean;
  showPluginInformation: boolean;
}

export const BookingInformationMessages: FC<BookingInformationMessagesProps> = ({
  arrivalNotepadInfo,
  bookingNo,
  hazardousGoodsApprovalInfo,
  hazardousGoodsApproved,
  loadingPreferenceApprovalInfo,
  livestockInfoText,
  showHazGoodsPaymentInformation,
  autoCancellation,
  showPluginInformation,
  showEnsGoodsBanner,
}) => {
  return (
    <Box spacing gap>
      {autoCancellation && <AutoCancellationInfoBanner info={autoCancellation} />}
      {hazardousGoodsApprovalInfo && (
        <>
          <Banner
            icon={stenaHazardous}
            variant={resolveInformationMessageBannerVariant(hazardousGoodsApprovalInfo.type)}
            text={hazardousGoodsApprovalInfo.descriptionText}
            contentRight={
              hazardousGoodsApproved && (
                <FlatButtonLink
                  to={routeCreators.hazardousGoods({ bookingNo })}
                  label={'See details'}
                />
              )
            }
          />
          {showHazGoodsPaymentInformation && (
            <Banner
              icon={stenaBusinessCard}
              variant={'info'}
              text={Information.HazardousGoodsConfirmedPayableOnline}
            />
          )}
        </>
      )}

      {loadingPreferenceApprovalInfo && (
        <Banner
          variant={resolveInformationMessageBannerVariant(loadingPreferenceApprovalInfo.type)}
          text={loadingPreferenceApprovalInfo.descriptionText}
        />
      )}

      {arrivalNotepadInfo && (
        <Banner
          variant={resolveInformationMessageBannerVariant(arrivalNotepadInfo.type)}
          text={arrivalNotepadInfo.descriptionText}
        />
      )}

      {livestockInfoText && (
        <Banner
          icon={stenaAnimals}
          variant={resolveInformationMessageBannerVariant(InformationMessageType.SoftWarning)}
          text={livestockInfoText}
        />
      )}

      {showPluginInformation && (
        <Banner icon={stenaPlugin} variant={'info'} text={Information.Plugin} />
      )}

      {showEnsGoodsBanner && <GoodsDescriptionsEnsHintBanner />}
    </Box>
  );
};
